import React from "react";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { IWebPageURL } from "../../../models/nominals";
import { ICompareTile, ITileID } from "../../../models/product-compare";
import { notEmpty } from "../../../utils/functional";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { SelectedProductTray as SelectedProductTrayComponent } from "../components/SelectedProductTray";
import { defaults } from "../defaults";
import { CompareAllModal as CompareAllModalComponent } from "../elements/CompareAllModal";
import {
    collapseTray,
    expandTray,
    hideModal,
    removeFromSelected,
} from "../reducers";

import styles from "./SelectedProductTrayContainer.module.scss";

const getSelectedTiles = (
    selectedTileIDs: readonly ITileID[],
    tiles: readonly ICompareTile[],
): ICompareTile[] => {
    // Find within map is used so as to preserve the selection order of the tiles.
    return selectedTileIDs
        .map((tileID) => {
            return tiles.find((t) => {
                return t.product_compare_tile.id === tileID;
            });
        })
        .filter(notEmpty);
};

const buildCompareAllURI = (
    tiles: readonly ICompareTile[],
    productComparePageURL: IWebPageURL | null,
): string => {
    const window = getWindow();
    const tileIDs = tiles.map((tile) => {
        return tile.product_compare_tile.id;
    });
    const baseURL = productComparePageURL;
    const ids = encodeURIComponent(tileIDs.join(","));
    const returnURL = window
        ? encodeURIComponent(window.location.pathname)
        : "";

    return `${baseURL}?tiles=${ids}&return_url=${returnURL}`;
};

export const SelectedProductTray = (props: {
    productComparePageURL: IWebPageURL | null;
    tiles: readonly ICompareTile[];
    ctaText?: string;
}): React.ReactNode => {
    const dispatch = useAppDispatch();

    const state = useAppSelector((rootState) => {
        return rootState.productcompare || defaults;
    });

    const ctaText = props.ctaText ? props.ctaText : "Compare All Mattresses";

    const onRemoveButtonClick = (tileID: ITileID) => {
        dispatch(removeFromSelected({ tileID }));
    };

    const onCollapseToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (state.trayIsExpanded) {
            dispatch(collapseTray());
        } else {
            dispatch(expandTray());
        }
    };

    const selectedTiles = getSelectedTiles(state.selectedTileIDs, props.tiles);
    const compareAllURI = buildCompareAllURI(
        props.tiles,
        props.productComparePageURL,
    );

    return (
        <section className={`selected-product-tray ${styles.root}`}>
            <SelectedProductTrayComponent
                selectedTiles={selectedTiles}
                compareAllURI={compareAllURI}
                priceSelectValue={state.priceSelectValue}
                productComparePageURL={props.productComparePageURL}
                onRemoveButtonClick={onRemoveButtonClick}
                onCollapseToggle={onCollapseToggle}
                isExpanded={state.trayIsExpanded}
                ariaLabel={state.trayLabel}
                numProductsAvailable={props.tiles.length}
                ctaText={ctaText}
            />
            <CompareAllModalComponent
                selectedTiles={selectedTiles}
                isModalVisible={state.showCompareModal}
                compareAllURI={compareAllURI}
                productComparePageURL={props.productComparePageURL}
                onCloseModalClick={() => dispatch(hideModal())}
            />
        </section>
    );
};
